import { useQuery } from 'react-query';
import axios from 'axios';

import { backendRequestHeaders, getBackendBaseUrl, resolveBearerToken } from '@/api/backendAuth';
import { useAppSelector } from '@/app/hooks';
import { useAuth } from '@/modules/Auth/hooks/useAuth';
import { selectCurrentWorkspace } from '@/modules/Dashboard/Workspaces/workspaceSlice';

import queryKeys from '../../modules/shared/query/queryKeys';

export interface TicketActivityRow {
  id: string;
  kind: string;
  summary: string;
  createdBy: string;
  createdByName: string;
  createdByEmail: string;
  createdAt: string;
}

function extractWorkspaceId(workspaceObj: unknown): string {
  const w = workspaceObj as { id?: string; SK?: string } | null | undefined;
  if (!w) return '';
  if (w.id) return w.id;
  if (w.SK) {
    if (w.SK.startsWith('WS#')) return w.SK.substring(3);
    return w.SK;
  }
  return '';
}

function normalizeActivity(raw: unknown): TicketActivityRow {
  const r = raw as Record<string, unknown>;
  return {
    id: String(r.id ?? ''),
    kind: String(r.kind ?? ''),
    summary: String(r.summary ?? ''),
    createdBy: String(r.createdBy ?? ''),
    createdByName: String(r.createdByName ?? ''),
    createdByEmail: String(r.createdByEmail ?? ''),
    createdAt: typeof r.createdAt === 'string' ? r.createdAt : new Date().toISOString(),
  };
}

async function fetchActivity(
  ticketId: string,
  sessionJwt: string | undefined,
  workspaceId: string,
): Promise<TicketActivityRow[]> {
  const bearer = resolveBearerToken(sessionJwt);
  const headers = backendRequestHeaders(bearer || undefined);
  if (!headers.Authorization) {
    throw new Error('Missing Bearer token. Please sign in again.');
  }

  const { data } = await axios.get<unknown[]>(
    `${getBackendBaseUrl()}/workspaces/${workspaceId}/tickets/${ticketId}/activity`,
    { headers, params: { limit: 80 } },
  );
  if (!Array.isArray(data)) return [];
  return data.map(normalizeActivity);
}

export function useGetTicketActivity(ticketId: string | null) {
  const { user } = useAuth();
  const workspace = useAppSelector(selectCurrentWorkspace);
  const workspaceId = extractWorkspaceId(workspace);
  const sessionJwt = user?.signInUserSession?.idToken?.jwtToken;
  const hasBearer = !!resolveBearerToken(sessionJwt);

  return useQuery({
    queryKey: [queryKeys.ticketActivity, ticketId, workspaceId],
    queryFn: () => {
      if (!ticketId) throw new Error('No ticket');
      return fetchActivity(ticketId, sessionJwt, workspaceId);
    },
    enabled: !!ticketId && !!workspaceId && hasBearer,
    staleTime: 15_000,
  });
}
